import { useEffect, useState } from 'react';

import {
  ALLOW_GRANT,
  DENY_GRANT,
  ALLOW_LOCATIONS,
  GA_DENY_DEFAULT_REGIMES,
  GA_ALLOW_DEFAULT_REGIMES,
} from './constants';

import { TRANSCEND_CONSENT_CHANGE_EVENT_NAME } from '@/modules/consent/constants';
import { useConsentManager } from '@/modules/consent/hooks/useConsentManager';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import { SupportedLocation } from '@/modules/location/constants';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';

export type CustomWindow = Window & {
  [key: string]: boolean;
};

export function useGTM(trackingCode: string) {
  const { airgap } = useConsentManager();
  const { location } = useCurrentLocation();
  const {
    gtm: { sendEvent: sendGTMEvent },
  } = useExternalTracking();

  const [hasInitialised, setHasInitialised] = useState(false);

  function getDefaultGAConsent(location: string) {
    // GTM requires a default consent state before anything else runs, but the
    // US privacy regime (CPRA / US_DNSS / US_OTHER) that decides the correct
    // default isn't known until airgap has loaded, which happens after this.
    // Those regimes disagree on the default, so we start the US fully denied and
    // let the reconciliation update (see the [airgap] effect below) grant consent
    // once the regime and the user's choices are known. Other locations have no
    // regime variance, so location alone determines the default there.
    const defaultGrant =
      location !== SupportedLocation.US && ALLOW_LOCATIONS.includes(location)
        ? ALLOW_GRANT
        : DENY_GRANT;
    return {
      ad_storage: defaultGrant,
      ad_user_data: defaultGrant,
      ad_personalization: defaultGrant,
      analytics_storage: defaultGrant,
    };
  }

  function getGAConsent() {
    const consentChoices = airgap?.getConsent();
    const [regime] = Array.from(airgap?.getRegimes() || []);
    const advertising = consentChoices?.purposes.Advertising;
    const saleOfInfo = consentChoices?.purposes.SaleOfInfo;
    const isAllowRegime = Boolean(
      regime && GA_ALLOW_DEFAULT_REGIMES.includes(regime)
    );
    const isDenyRegime = Boolean(
      regime && GA_DENY_DEFAULT_REGIMES.includes(regime)
    );
    // Outside the US, ad_* follows advertising consent (existing behaviour).
    // For US regimes, sale of info also gates ad_* (denied if opted out):
    //  - US_OTHER: granted unless sale of info is opted out ('auto' grants)
    //  - US_DNSS: granted only with an explicit sale of info opt-in ('auto' denies)
    //  - CPRA: needs both advertising AND sale of info (opt out of either denies)
    const saleOfInfoGrants = isAllowRegime
      ? saleOfInfo !== false
      : saleOfInfo === true;
    const adGranted =
      isAllowRegime || isDenyRegime
        ? saleOfInfoGrants && (regime !== 'CPRA' || advertising === true)
        : advertising;
    return {
      ad_storage: adGranted ? ALLOW_GRANT : DENY_GRANT,
      ad_user_data: adGranted ? ALLOW_GRANT : DENY_GRANT,
      ad_personalization: adGranted ? ALLOW_GRANT : DENY_GRANT,
      analytics_storage: consentChoices?.purposes.Analytics
        ? ALLOW_GRANT
        : DENY_GRANT,
    };
  }

  function setGoogleObjectsOnWindow() {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        if (window.dataLayer) {
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer.push(arguments);
        }
      };
  }

  function toggleOptOut(shouldDisable: boolean) {
    (window as unknown as CustomWindow)[`ga-disable-${trackingCode}`] =
      shouldDisable;
  }

  function handleConsentChange() {
    const consent = airgap?.getConsent();
    if (hasInitialised) {
      toggleOptOut(
        Boolean(consent?.purposes.Advertising && consent.purposes.Analytics)
      );
    }
    // this won't do anything if 'do not track' is enabled
    // as there won't be a gtag function on the window
    if (window.gtag) {
      window.gtag('consent', 'update', getGAConsent());
    }
    sendGTMEvent({
      event: 'consent_update',
      consent: getGAConsent(),
    });
  }

  useEffect(() => {
    if (!hasInitialised || !airgap) {
      return;
    }
    airgap.addEventListener(
      TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
      handleConsentChange
    );
    return () => {
      airgap.removeEventListener(
        TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
        handleConsentChange
      );
    };
  }, [hasInitialised, airgap]);

  useEffect(() => {
    if (hasInitialised || navigator.doNotTrack === '1') {
      return;
    }
    setGoogleObjectsOnWindow();
    // TS still requires the check
    if (window.gtag) {
      // we have to set the default again here because this can also get called on a full page refresh
      // if we do not send the default first, then GTM never initialises consent and the update
      // does not take effect
      window.gtag('consent', 'default', getDefaultGAConsent(location));
    }
    setHasInitialised(true);
  }, [hasInitialised]);

  useEffect(() => {
    if (airgap && window.gtag) {
      window.gtag('consent', 'update', getGAConsent());
    }
  }, [airgap]);
}
